import React from 'react'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { Dialog, useTheme } from '@mui/material'
import { type Breakpoint } from '@mui/system'
import { StyledDialogTitle } from './styles'
import { ButtonType } from '../GenericButton/styles'
import { grey } from '../../colors'
import DefaultModalActions from './DefaultModalActions'
import { capitalizeFirst } from '../../utils/utils'

export enum ModalActions {
  PRIMARY = 'primary',
  SUCCESS = 'success',
  ERROR = 'error',
  INFORMATION = 'info',
  WARNING = 'warning'
}
interface GenericModalProps {
  isOpen: boolean
  onClose: (e?: React.MouseEvent<HTMLElement>) => void
  onConfirm?: () => Promise<void>
  title?: string | React.ReactElement | React.ReactNode
  children?: React.ReactElement | React.ReactNode
  action?: boolean | React.ReactElement
  showCloseIcon?: boolean
  confirmActionText?: string
  confirmActionType?: ButtonType
  confirmButtonDisabled?: boolean
  hideConfirmButton?: boolean
  hideDestructiveButton?: boolean
  modalWidth?: string
  maxWidth?: string
  headerWrapper?: any
  destructiveButtonType?: ButtonType
  destructiveButtonText?: string
  destructiveButtonDisabled?: boolean
  destructiveButtonLoading?: boolean
  confirmButtonLoading?: boolean
  height?: string
  /**
   * Keep focus enforcement off when the modal needs to host a portaled popup,
   * such as the survey display-conditions Values autocomplete.
   */
  disableEnforceFocus?: boolean
}

const GenericModal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  showCloseIcon = true,
  action = true,
  confirmActionText = 'Confirm',
  confirmButtonDisabled = false,
  confirmActionType = ButtonType.Primary,
  hideConfirmButton = false,
  hideDestructiveButton = false,
  modalWidth,
  maxWidth = 'md',
  headerWrapper,
  destructiveButtonType = ButtonType.Ghost,
  destructiveButtonText = 'Cancel',
  destructiveButtonDisabled,
  destructiveButtonLoading = false,
  confirmButtonLoading = false,
  height = 'not-set',
  disableEnforceFocus = false
}) => {
  const Icon = (
    <IconButton onClick={(e) => { e.stopPropagation(); onClose(e) }} sx={{ padding: 0, color: grey[150] }} >
      <CloseIcon />
    </IconButton>
  )

  const handleClose = (event: React.SyntheticEvent, reason: string): void => {
    event.stopPropagation()
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      console.log('Prevented Enter keypress from closing')
      return
    }
    onClose(event as React.MouseEvent<HTMLElement>)
  }

  const theme = useTheme()

  const modalActions = typeof action === 'boolean' && action

    ? <DefaultModalActions
      confirmButtonProps={{
        onClick: onConfirm,
        text: confirmActionText,
        disabled: confirmButtonDisabled,
        buttonType: confirmActionType,
        loading: confirmButtonLoading,
        hideButton: hideConfirmButton
      }}
      destructiveButtonProps={{
        onClick: onClose,
        text: destructiveButtonText,
        disabled: destructiveButtonDisabled,
        buttonType: destructiveButtonType,
        loading: destructiveButtonLoading,
        hideButton: hideDestructiveButton
      }}

    />
    : action

  const dialogStyle: React.CSSProperties = {
    borderRadius: '12px',
    width: modalWidth,
    height,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    minHeight: 0,
    overflow: 'auto',
    paddingBottom: theme.spacing(17)
  }

  const contentStyle = {
    padding: `${theme.spacing(4)} ${theme.spacing(6)} 0`,
    borderBottom: 'none',
    boxShadow: 'none',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto'
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth as Breakpoint}
      PaperProps={{ style: dialogStyle }}
      disableEscapeKeyDown
      disableEnforceFocus={disableEnforceFocus}
    >
      <StyledDialogTitle sx={headerWrapper}>
        {typeof title === 'string' ? capitalizeFirst(title) : title}
        {showCloseIcon && Icon}
      </StyledDialogTitle>
      <DialogContent sx={contentStyle}>
        {children}
      </DialogContent>
      {modalActions}
    </Dialog>
  )
}

export default GenericModal
